import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Spinner, ButtonGroup, Button } from 'react-bootstrap';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Scatter, Bar, Doughnut, Line, Radar, Pie } from 'react-chartjs-2';
import { chatAPI } from '../api';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  Filler
);

const ChartsPage = () => {
  const [traders, setTraders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeChart, setActiveChart] = useState('overview');

  useEffect(() => {
    fetchTraders();
  }, []);

  const fetchTraders = async () => {
    try {
      const response = await chatAPI.getTraders();
      console.log('Charts API Response:', response.data);
      
      const tradersData = Array.isArray(response.data) ? response.data : [];
      setTraders(tradersData);
    } catch (error) {
      console.error('Fetch error:', error);
      setTraders([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <div className="text-center mt-5">
      <Spinner animation="border" variant="primary" />
    </div>
  );

  const cobaltColors = {
    primary: 'rgba(0, 71, 171, 0.8)',
    light: 'rgba(30, 136, 229, 0.8)',
    success: 'rgba(72, 187, 120, 0.8)',
    warning: 'rgba(237, 137, 54, 0.8)',
    danger: 'rgba(245, 101, 101, 0.8)',
    info: 'rgba(66, 153, 225, 0.8)',
    purple: 'rgba(159, 122, 234, 0.8)',
    pink: 'rgba(237, 100, 166, 0.8)'
  };

  const colorArray = [
    cobaltColors.primary,
    cobaltColors.light,
    cobaltColors.success,
    cobaltColors.warning,
    cobaltColors.info,
    cobaltColors.purple,
    cobaltColors.pink,
    cobaltColors.danger
  ];

  // ============= 차트 1: 승률 vs 샤프 비율 산점도 =============
  const scatterData = {
    datasets: [{
      label: '트레이더 분포',
      data: traders.map(t => ({
        x: t.performance?.win_rate || 0,
        y: t.performance?.sharpe_ratio || 0
      })),
      backgroundColor: cobaltColors.primary,
      borderColor: 'rgba(0, 71, 171, 1)',
      borderWidth: 2,
      pointRadius: 6,
      pointHoverRadius: 8
    }]
  };

  const scatterOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: { 
        display: true,
        labels: { font: { family: 'Pretendard', size: 12 } }
      },
      title: { 
        display: true, 
        text: '승률 vs 샤프 비율 분포',
        font: { family: 'Pretendard', size: 16, weight: 'bold' }
      }
    },
    scales: {
      x: { 
        title: { display: true, text: '승률 (%)', font: { family: 'Pretendard', size: 12 } },
        grid: { color: 'rgba(0, 0, 0, 0.05)' }
      },
      y: { 
        title: { display: true, text: '샤프 비율', font: { family: 'Pretendard', size: 12 } },
        grid: { color: 'rgba(0, 0, 0, 0.05)' }
      }
    }
  };

  // ============= 차트 2: 스타일별 평균 승률 =============
  const styles = {};
  traders.forEach(t => {
    const style = t.profile?.trading_style || 'Unknown';
    if (!styles[style]) styles[style] = { total: 0, count: 0 };
    styles[style].total += t.performance?.win_rate || 0;
    styles[style].count += 1;
  });

  const barData = {
    labels: Object.keys(styles),
    datasets: [{
      label: '평균 승률',
      data: Object.values(styles).map(s => (s.total / s.count).toFixed(1)),
      backgroundColor: colorArray.slice(0, Object.keys(styles).length),
      borderWidth: 2
    }]
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: { display: false },
      title: { 
        display: true, 
        text: '트레이딩 스타일별 평균 승률',
        font: { family: 'Pretendard', size: 16, weight: 'bold' }
      }
    },
    scales: {
      y: { 
        beginAtZero: true,
        max: 100,
        title: { display: true, text: '승률 (%)', font: { family: 'Pretendard' } },
        grid: { color: 'rgba(0, 0, 0, 0.05)' }
      },
      x: {
        grid: { display: false }
      }
    }
  };

  // ============= 차트 3: 스타일별 분포 (파이 차트) =============
  const styleKeys = Object.keys(styles);
  const pieData = {
    labels: styleKeys,
    datasets: [{
      label: '트레이더 수',
      data: styleKeys.map(key => styles[key].count),
      backgroundColor: styleKeys.map((_, i) => colorArray[i % colorArray.length]),
      borderColor: '#fff',
      borderWidth: 2,
      hoverOffset: 4
    }]
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: { 
        position: 'right',
        labels: { font: { family: 'Pretendard', size: 12 } }
      },
      title: { 
        display: true, 
        text: '트레이딩 스타일별 분포',
        font: { family: 'Pretendard', size: 16, weight: 'bold' }
      }
    }
  };
  // ============= 차트 4: 리스크 레벨 분포 (도넛 차트) =============
  const riskLevels = {};
  traders.forEach(t => {
    const risk = t.profile?.risk_tolerance || 'Unknown';
    riskLevels[risk] = (riskLevels[risk] || 0) + 1;
  });

  const riskKeys = Object.keys(riskLevels);
  const doughnutData = {
    labels: riskKeys,
    datasets: [{
      label: '트레이더 수',
      data: riskKeys.map(key => riskLevels[key]),
      backgroundColor: riskKeys.map((_, i) => [
        cobaltColors.success,
        cobaltColors.warning,
        cobaltColors.danger,
        cobaltColors.info
      ][i % 4]),
      borderColor: '#fff',
      borderWidth: 3,
      hoverOffset: 4
    }]
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: { 
        position: 'right',
        labels: { font: { family: 'Pretendard', size: 12 } }
      },
      title: { 
        display: true, 
        text: '리스크 레벨 분포',
        font: { family: 'Pretendard', size: 16, weight: 'bold' }
      }
    }
  };

  // ============= 차트 5: 상위 5명 종합 지표 (레이더 차트) =============
  const top5 = [...traders]
    .sort((a, b) => (b.performance?.sharpe_ratio || 0) - (a.performance?.sharpe_ratio || 0))
    .slice(0, 5);

  const radarData = {
    labels: ['승률', '샤프비율', '손익비', '거래빈도', '안정성'],
    datasets: top5.map((trader, index) => {
      const perf = trader.performance || {};
      return {
        label: trader.profile?.name || 'Unknown',
        data: [
          (perf.win_rate || 0) / 100 * 10,
          Math.min((perf.sharpe_ratio || 0) * 3, 10),
          Math.min((perf.profit_factor || 0) * 2, 10),
          Math.min((perf.total_trades || 0) / 5, 10),
          Math.max(10 - Math.abs(perf.max_drawdown_pct || 0) / 5, 0)
        ],
        backgroundColor: colorArray[index].replace('0.8', '0.2'),
        borderColor: colorArray[index],
        borderWidth: 2
      };
    })
  };

  const radarOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: { 
        position: 'bottom',
        labels: { font: { family: 'Pretendard', size: 11 } }
      },
      title: { 
        display: true, 
        text: '상위 5명 종합 성과 비교',
        font: { family: 'Pretendard', size: 16, weight: 'bold' }
      }
    },
    scales: {
      r: {
        beginAtZero: true,
        max: 10,
        ticks: { 
          stepSize: 2,
          font: { family: 'Pretendard', size: 10 }
        }
      }
    }
  };

  // ============= 차트 6: 경력별 평균 성과 (라인 차트) =============
  const experienceGroups = {};
  traders.forEach(t => {
    const exp = t.profile?.years_experience || 0;
    let group;
    if (exp <= 2) group = '0-2년';
    else if (exp <= 5) group = '3-5년';
    else if (exp <= 10) group = '6-10년';
    else group = '10년+';
    
    if (!experienceGroups[group]) {
      experienceGroups[group] = {
        winRate: [],
        sharpe: [],
        pnl: []
      };
    }
    experienceGroups[group].winRate.push(t.performance?.win_rate || 0);
    experienceGroups[group].sharpe.push(t.performance?.sharpe_ratio || 0);
    experienceGroups[group].pnl.push(t.performance?.total_pnl || 0);
  });

  const expLabels = ['0-2년', '3-5년', '6-10년', '10년+'];
  const lineData = {
    labels: expLabels,
    datasets: [
      {
        label: '평균 승률 (%)',
        data: expLabels.map(label => {
          const group = experienceGroups[label];
          if (!group) return 0;
          return (group.winRate.reduce((a, b) => a + b, 0) / group.winRate.length).toFixed(1);
        }),
        borderColor: cobaltColors.primary,
        backgroundColor: cobaltColors.primary.replace('0.8', '0.2'),
        tension: 0.4,
        fill: true
      },
      {
        label: '평균 샤프 비율',
        data: expLabels.map(label => {
          const group = experienceGroups[label];
          if (!group) return 0;
          return (group.sharpe.reduce((a, b) => a + b, 0) / group.sharpe.length * 20).toFixed(1);
        }),
        borderColor: cobaltColors.success,
        backgroundColor: cobaltColors.success.replace('0.8', '0.2'),
        tension: 0.4,
        fill: true
      }
    ]
  };

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: { 
        position: 'top',
        labels: { font: { family: 'Pretendard', size: 12 } }
      },
      title: { 
        display: true, 
        text: '경력별 평균 성과 추이',
        font: { family: 'Pretendard', size: 16, weight: 'bold' }
      }
    },
    scales: {
      y: { 
        beginAtZero: true,
        title: { display: true, text: '지표 값', font: { family: 'Pretendard' } },
        grid: { color: 'rgba(0, 0, 0, 0.05)' }
      },
      x: {
        grid: { display: false }
      }
    }
  };
  return (
    <div>
      <h2 className="mb-4">
        <i className="fas fa-chart-bar me-2"></i>
        차트 분석
      </h2>

      {/* 차트 선택 버튼 */}
      <Card className="mb-4">
        <Card.Body>
          <ButtonGroup className="w-100 flex-wrap">
            <Button 
              variant={activeChart === 'overview' ? 'primary' : 'outline-primary'}
              onClick={() => setActiveChart('overview')}
              className="mb-2"
            >
              <i className="fas fa-th me-2"></i>
              전체 보기
            </Button>
            <Button 
              variant={activeChart === 'scatter' ? 'primary' : 'outline-primary'}
              onClick={() => setActiveChart('scatter')}
              className="mb-2"
            >
              <i className="fas fa-dot-circle me-2"></i>
              산점도
            </Button>
            <Button 
              variant={activeChart === 'distribution' ? 'primary' : 'outline-primary'}
              onClick={() => setActiveChart('distribution')}
              className="mb-2"
            >
              <i className="fas fa-chart-pie me-2"></i>
              분포 차트
            </Button>
            <Button 
              variant={activeChart === 'comparison' ? 'primary' : 'outline-primary'}
              onClick={() => setActiveChart('comparison')}
              className="mb-2"
            >
              <i className="fas fa-chart-area me-2"></i>
              비교 차트
            </Button>
          </ButtonGroup>
        </Card.Body>
      </Card>

      {/* 전체 보기 */}
      {activeChart === 'overview' && (
        <Row>
          <Col lg={6} className="mb-4">
            <Card className="h-100">
              <Card.Header>
                <i className="fas fa-dot-circle me-2"></i>
                승률 vs 샤프 비율
              </Card.Header>
              <Card.Body>
                <Scatter data={scatterData} options={scatterOptions} />
              </Card.Body>
            </Card>
          </Col>
          <Col lg={6} className="mb-4">
            <Card className="h-100">
              <Card.Header>
                <i className="fas fa-chart-bar me-2"></i>
                스타일별 승률
              </Card.Header>
              <Card.Body>
                <Bar data={barData} options={barOptions} />
              </Card.Body>
            </Card>
          </Col>
          <Col lg={6} className="mb-4">
            <Card className="h-100">
              <Card.Header>
                <i className="fas fa-chart-pie me-2"></i>
                스타일 분포
              </Card.Header>
              <Card.Body>
                <Pie data={pieData} options={pieOptions} />
              </Card.Body>
            </Card>
          </Col>
          <Col lg={6} className="mb-4">
            <Card className="h-100">
              <Card.Header>
                <i className="fas fa-exclamation-triangle me-2"></i>
                리스크 레벨
              </Card.Header>
              <Card.Body>
                <Doughnut data={doughnutData} options={doughnutOptions} />
              </Card.Body>
            </Card>
          </Col>
          <Col lg={12} className="mb-4">
            <Card>
              <Card.Header>
                <i className="fas fa-radar me-2"></i>
                상위 5명 비교
              </Card.Header>
              <Card.Body>
                <Radar data={radarData} options={radarOptions} />
              </Card.Body>
            </Card>
          </Col>
          <Col lg={12} className="mb-4">
            <Card>
              <Card.Header>
                <i className="fas fa-chart-line me-2"></i>
                경력별 추이
              </Card.Header>
              <Card.Body>
                <Line data={lineData} options={lineOptions} />
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* 산점도 뷰 */}
      {activeChart === 'scatter' && (
        <Row>
          <Col lg={12} className="mb-4">
            <Card>
              <Card.Header>
                <i className="fas fa-dot-circle me-2"></i>
                승률 vs 샤프 비율 분포
              </Card.Header>
              <Card.Body style={{ height: '600px' }}>
                <Scatter data={scatterData} options={{...scatterOptions, maintainAspectRatio: false}} />
              </Card.Body>
            </Card>
          </Col>
          <Col lg={12} className="mb-4">
            <Card>
              <Card.Header>
                <i className="fas fa-chart-bar me-2"></i>
                스타일별 평균 승률
              </Card.Header>
              <Card.Body style={{ height: '400px' }}>
                <Bar data={barData} options={{...barOptions, maintainAspectRatio: false}} />
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
      {/* 분포 차트 뷰 */}
      {activeChart === 'distribution' && (
        <Row>
          <Col lg={6} className="mb-4">
            <Card className="h-100">
              <Card.Header>
                <i className="fas fa-chart-pie me-2"></i>
                트레이딩 스타일 분포
              </Card.Header>
              <Card.Body style={{ height: '500px' }}>
                <Pie data={pieData} options={{...pieOptions, maintainAspectRatio: false}} />
              </Card.Body>
            </Card>
          </Col>
          <Col lg={6} className="mb-4">
            <Card className="h-100">
              <Card.Header>
                <i className="fas fa-exclamation-triangle me-2"></i>
                리스크 레벨 분포
              </Card.Header>
              <Card.Body style={{ height: '500px' }}>
                <Doughnut data={doughnutData} options={{...doughnutOptions, maintainAspectRatio: false}} />
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* 비교 차트 뷰 */}
      {activeChart === 'comparison' && (
        <Row>
          <Col lg={12} className="mb-4">
            <Card>
              <Card.Header>
                <i className="fas fa-chart-radar me-2"></i>
                상위 5명 종합 성과 비교
              </Card.Header>
              <Card.Body style={{ height: '600px' }}>
                <Radar data={radarData} options={{...radarOptions, maintainAspectRatio: false}} />
              </Card.Body>
            </Card>
          </Col>
          <Col lg={12} className="mb-4">
            <Card>
              <Card.Header>
                <i className="fas fa-chart-line me-2"></i>
                경력별 평균 성과 추이
              </Card.Header>
              <Card.Body style={{ height: '400px' }}>
                <Line data={lineData} options={{...lineOptions, maintainAspectRatio: false}} />
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default ChartsPage;